import { canonicalThumbSource, encodeThumbSource } from "./static-thumb-contract.ts";

/**
 * 小图 URL 的唯一实现，服务端与管端共用。静态映射缺失时由读图入口回原图。
 * 服务端从 media.ts 再导出，调用方照旧 `import { thumbUrl } from "../media.ts"`。
 */
/**
 * 小图档位（按显示用途命名，**调用方只引这里的名字，不写数字**）。值是输出宽度（像素），
 * 高度按原图比例。CDN 只缩不放：原图比档位小时原样尺寸输出。
 *
 * 数字按客户端设计分辨率 402 宽、fitWidth、最宽手机 1440px（×3.6）量出的各图位最大像素定
 *（fly-game-app 2026-10-22 逐个图位量的），平板没算。
 *
 * 档位与 static-thumb-contract.ts 的上传规格一致。改规格需同步上传与补图，并增加资源 BUILDER_VERSION。
 */
export const THUMB_TIERS = {
  /** 物品说明、成就这类小图标（最大显示约 61–150px） */
  small: 160,
  /** 头像、头像框、天赋图标、卡包封面（最大显示约 140–330px；个人页大头像仍用原图） */
  icon: 320,
  /** 卡面缩略、选关卡片、商城格子、拼图列表图（最大显示约 400–640px） */
  card: 640,
  /** 建造页拼图大图（板边长约 1142px） */
  board: 1152,
} as const;
export type ThumbTier = keyof typeof THUMB_TIERS;

/**
 * 原图域名 → 对应环境的公开静态小图入口。文件仍在原有图床。
 */
const STATIC_THUMB_ORIGINS: Record<string, string> = {
  "pbtest.buyacard.cc": "https://aeroplane.pwtk-dev.work",
  // 生产两个域名指向同一个 Worker，取 idolcard；test 配置里残留的 pp 图也走生产入口（2026-11-04 只剩 2 张）。
  "resource.bingo84.com": "https://aeroplane.idolcard.cc",
};

/**
 * 原图 URL → 静态小图入口，纯字符串变换，不给业务接口增加 SQL。
 * 入口缓存原图到静态 WebP 的跳转；旧图未补齐时按 thumbFallbackUrl 降级。
 * 外链、带查询参数的图和已处理的 URL 原样返回。
 */
export function thumbUrl(url: string, tier: ThumbTier): string {
  if (!url) return url;
  let u: URL;
  try { u = new URL(url); } catch { return url; }
  const origin = STATIC_THUMB_ORIGINS[u.host];
  if (!origin || !canonicalThumbSource(url)) return url;
  return `${origin}/media/thumb/v1/${THUMB_TIERS[tier]}/${encodeThumbSource(u.href)}`;
}

/**
 * 没有静态映射（旧图未补、读库失败）时 302 去哪：pbtest 走 test 账户的转换入口；
 * 生产图回到切静态小图之前一直在用的 CDN 现场缩图，补图前后玩家拿到的都不比原来差。其余回原图。
 */
export function thumbFallbackUrl(source: string, width: number): string {
  const u = new URL(source);
  if (u.host === "pbtest.buyacard.cc") return `https://aeroplane.pwtk-dev.work/media/thumb-fallback/${width}/${encodeThumbSource(u.href)}`;
  if (u.host === "resource.bingo84.com") return `${u.origin}/cdn-cgi/image/width=${width},format=webp${u.pathname}`;
  return source;
}
